import React, { useMemo } from "react";
import { ErrorMessage } from "@hookform/error-message";
import { Select, TextField, Typography } from "@mui/material";
import { RefCallBack, useController, UseControllerProps } from "react-hook-form";
import { FormProps } from "./FormProps";
import ControlledInput from "../CustomInput/ControlledInput";
import CustomSwitch from "../CustomInput/CustomSwitch";
import Autocomplete from "@mui/material/Autocomplete";

function FormField(props: {
  name: string;
  rules?: UseControllerProps["rules"];
  defaultValue?: UseControllerProps["defaultValue"];
  disabled?: boolean;
  Field: typeof TextField | typeof ControlledInput | typeof CustomSwitch | typeof Select | typeof Autocomplete;
  fieldProps:
    | ((params: {
        onChange: (...event: any) => void;
        value: any;
        onBlur: () => void;
        ref: RefCallBack;
      }) => Partial<FormProps>)
    | Partial<FormProps>;
  fieldChildren:
    | ((params: {
        onChange: (...event: any) => void;
        value: any;
        onBlur: () => void;
        ref: RefCallBack;
      }) => Partial<FormProps>)
    | Partial<FormProps>;
}) {
  const {
    field: { value, onChange, onBlur, ref },
    fieldState: { error },
    formState: { errors },
  } = useController({ name: props.name, rules: props.rules, defaultValue: props.defaultValue });

  const fieldProps = useMemo(() => {
    return typeof props.fieldProps === "function"
      ? props.fieldProps({ onChange, value, onBlur, ref })
      : props.fieldProps;
  }, [props, onChange, value, onBlur, ref]);

  return (
    <>
      <props.Field
        color={"primary"}
        disabled={props.disabled}
        error={!!error}
        inputRef={ref}
        name={props.name}
        onBlur={onBlur}
        onChange={onChange}
        style={{ width: "100%", backgroundColor: "rgba(100, 100, 100, 0.05)" }}
        value={value}
        {...fieldProps}
      >
        {typeof props.fieldChildren === "function"
          ? props.fieldChildren({ onChange, onBlur, value, ref })
          : props.fieldChildren}
      </props.Field>
      <ErrorMessage
        errors={errors}
        name={props.name}
        render={({ messages, message }) => {
          if (messages) {
            return Object.entries(messages).map(([type, message]) => (
              <Typography color={"error"} key={type}>
                {message}
              </Typography>
            ));
          } else if (message) {
            return <Typography color={"error"}>{message}</Typography>;
          } else {
            return null;
          }
        }}
      />
    </>
  );
}

export default FormField;
